/**
 * Impersonation step-up re-authentication gate (METALARK-1082)
 *
 * Decides whether the impersonation dialog should render a password field.
 * The backend enforces the same rule in ImpersonationController; this only
 * controls whether we ask for the password up front or make the user round-trip
 * through a 401. Both sides read the same two values off /auth/me:
 *
 *   authTime                        - Unix seconds of the last genuine login,
 *                                     preserved across silent token refreshes.
 *   impersonationReauthWindowMinutes - configured window; 0 = always prompt.
 *
 * Mirrors api/utils/auth/ReauthWindow.ts. Keep the two in sync.
 *
 * Call this when the dialog opens, not once at page load: /auth/me is cached in
 * Redux for the life of the page, so the window can elapse under a tab that
 * stays open. Even then the backend is authoritative — if the window closes
 * between the check and the submit, the endpoint answers 401
 * PASSWORD_CONFIRMATION_REQUIRED and the dialog reveals the field inline.
 */

/**
 * Whether a password-auth admin must re-enter their password to impersonate.
 *
 * Fails safe in every uncertain case — a missing user context, a missing
 * authTime (session predates the claim), an unparseable window, or a zero
 * window all return true. Guessing "false" wrongly would silently drop a
 * security prompt; guessing "true" wrongly only costs the admin a password
 * they were already used to typing.
 *
 * @param {object|null|undefined} userContext - the /auth/me payload
 * @param {number} [nowMs] - injectable clock, for tests
 * @returns {boolean}
 */
export function impersonationRequiresPassword(userContext, nowMs = Date.now()) {
    // Only password sessions are ever gated. Passkey / SSO / OAuth / federated
    // callers were already strongly verified and are never prompted.
    if (userContext?.authMethod !== 'password') {
        return false;
    }

    const authTime = Number(userContext?.authTime);
    if (!Number.isFinite(authTime) || authTime <= 0) {
        return true;
    }

    const windowMinutes = Number(userContext?.impersonationReauthWindowMinutes);
    if (!Number.isFinite(windowMinutes) || windowMinutes <= 0) {
        return true;
    }

    // Clamp negative elapsed (clock skew between the browser and the minting
    // Lambda) to zero rather than treating skew as an unbounded open window.
    const elapsedSeconds = Math.max(0, Math.floor(nowMs / 1000) - authTime);

    return elapsedSeconds >= windowMinutes * 60;
}
